import {
  BatchOperationState,
  CaseMediaState,
  FileViewMode,
  FolderPermissionFlags,
  FolderStateType,
  VirtualFolderNames,
} from '@redact-modules/casemedia/store';
import { FolderPathItem } from '@redact-modules/helper';
import { ROUTE_HOME } from '@common/state/modules/routing';
import {
  ActionFetchFolderRequest,
  ActionFetchMoreSubFoldersRequest,
  ActionSelectedFolderSuccessPayload,
  ActionDeleteFolderPayload,
  ActionSetSelectedVirtualFolderPayload,
  ActionSetSelectedFolderPayload,
  ActionDeleteFilePayload,
  ActionCreateExportTdoPayload,
  ActionExportFilePayload,
  ActionDownloadAllJobStatusPayload,
  ActionFetchExportTdoPayload,
  ActionDownloadAllStatusPayload,
  ActionCreateNewFolderPayload,
  ActionMoveFolderPayload,
  ActionRemoveSelectedFolderPayload,
  ActionNavToCaseMediaPayload,
  ActionFetchMoreFilesRequest,
  ActionDeleteCaseSdoPayload,
  ActionSetShowLegacyMediasPayload,
  ActionFetchLegacyMediasRequest,
  ActionFileTdoWithCasePayload,
  ActionDeleteMediaFileSdoPayload,
  ActionFetchTdoDetailForSdosPayload,
  ActionRenameFolderPayload,
  ActionFetchForExportedFolderPayload,
  ActionSetSourceFileToMovePayload,
  SearchNameExistsInFolderRequestPayload,
  ActionSetCaseMedia,
  ActionUpdateRedactionStatusChangePayload,
  ActionUpdateRedactionStatusRequestPayload,
  ActionSetMoveInputFilePayload,
  ActionMoveFileToCasePayload,
  SearchNameActions,
  ActionSoftDeleteCaseSdoPayload,
  ActionRestoredFolderPayload,
  ActionChangeFileFiltersPayload,
  ActionDownloadAllForTdoPayload,
  ActionExportMediaListPayload,
  ActionMarkFolderViewTdoOpenedPayload,
  ActionClearLastOpenedFolderViewFileForCasePayload,
  ActionSetLastActiveFolderViewFilePayload,
  ActionBatchTrashFilesPayload,
  ActionBatchMoveFilesPayload,
  ActionBatchUpdateStatusPayload,
  ActionBatchApplyWatermarkPayload,
} from '@redact-modules/casemedia/models';
import {
  CaseId,
  FolderId,
  TDOId,
  TreeObjectId,
} from '@common/state/modules/universal/models/Brands';
import {
  createGraphQLFailureAction,
  createGraphQLRequestAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import {
  CreateExportFilesQueryResponse,
  CreateRootFoldersQueryResponse,
  DeleteFileQueryResponse,
  DeleteFolderOrCaseQueryResponse,
  DeleteOutputFileQueryResponse,
  ExportFilesQueryResponse,
  FetchCaseContentsQueryResponse,
  FetchCaseOrFolderTreeQueryResponse,
  FetchExportTdoQueryResponse,
  FetchJobStatusQueryResponse,
  FetchLegacyMediasQueryResponse,
  FetchRootFoldersQueryResponse,
  FetchSubFoldersQueryResponse,
  FileTdoWithCaseQueryResponse,
  MoveFolderOrCaseQueryResponse,
  MoveTdoQueryResponse,
  RenameFolderQueryResponse,
  SearchNameExistInFolderQueryResponse,
  SoftDeleteFileQueryResponse,
  UpdateRedactionStatusQueryResponse,
} from '../services/queries';
import { FetchTdoDetailForSdosResponse } from '../services';
import type { StalenessAssetsResponse } from '../services/queries';
import { createAction } from '@reduxjs/toolkit';

interface FETCH_ROOT_FOLDER_VARIABLES {
  limit: number;
  offset: number;
}
export const FETCH_ROOT_FOLDERS_REQUEST =
  createAction<ActionFetchFolderRequest>(
    'CASEMEDIA/FETCH_ROOT_FOLDERS_REQUEST'
  );
export const FETCH_ROOT_FOLDERS_INIT = 'CASEMEDIA/FETCH_ROOT_FOLDERS_INIT';
export const FETCH_ROOT_FOLDERS_SUCCESS = createGraphQLSuccessAction<
  FetchRootFoldersQueryResponse,
  FETCH_ROOT_FOLDER_VARIABLES
>('CASEMEDIA/FETCH_ROOT_FOLDERS_SUCCESS');
export const FETCH_ROOT_FOLDERS_FAILURE = createGraphQLFailureAction<
  { message: string; name: string }[]
>('CASEMEDIA/FETCH_FOLDERS_FAILURE');

// Mirrors the `rootFolderSmartSelection` app-config flag into this slice so the
// root-folder reducers can apply it when resolving which root to use.
export const SET_ROOT_FOLDER_SMART_SELECTION = createAction<boolean>(
  'CASEMEDIA/SET_ROOT_FOLDER_SMART_SELECTION'
);

export const CREATE_ROOT_FOLDER_REQUEST = createAction(
  'CASEMEDIA/CREATE_ROOT_FOLDERS_REQUEST'
);
export const CREATE_ROOT_FOLDER_INIT = 'CASEMEDIA/CREATE_ROOT_FOLDERS_INIT';
export const CREATE_ROOT_FOLDER_SUCCESS =
  createGraphQLSuccessAction<CreateRootFoldersQueryResponse>(
    'CASEMEDIA/CREATE_ROOT_FOLDERS_SUCCESS'
  );
export const CREATE_ROOT_FOLDER_FAILURE = createGraphQLFailureAction<any>(
  'CASEMEDIA/CREATE_ROOT_FOLDERS_FAILURE'
);

interface FETCH_MORE_ROOTFOLDERS_VARIABLES {
  limit: number;
  offset: number;
}
export const FETCH_MORE_ROOTFOLDERS_REQUEST =
  createAction<ActionFetchFolderRequest>(
    'CASEMEDIA/FETCH_MORE_ROOTFOLDERS_REQUEST'
  );
export const FETCH_MORE_ROOTFOLDERS_INIT =
  'CASEMEDIA/FETCH_MORE_ROOTFOLDERS_INIT';
export const FETCH_MORE_ROOTFOLDERS_SUCCESS = createGraphQLSuccessAction<
  FetchRootFoldersQueryResponse,
  FETCH_MORE_ROOTFOLDERS_VARIABLES,
  true
>('CASEMEDIA/FETCH_MORE_ROOTFOLDERS_SUCCESS');
export const FETCH_MORE_ROOTFOLDERS_FAILURE = createGraphQLFailureAction(
  'CASEMEDIA/FETCH_MORE_ROOTFOLDERS_FAILURE'
);

interface FETCH_MORE_SUBFOLDERS_VARIABLES {
  folderId: FolderId;
  offset: number;
  limit: number;
}

export const FETCH_MORE_SUBFOLDERS_REQUEST =
  createAction<ActionFetchMoreSubFoldersRequest>(
    'CASEMEDIA/FETCH_MORE_SUBFOLDERS_REQUEST'
  );
export const FETCH_MORE_SUBFOLDERS_INIT = createGraphQLRequestAction<
  FETCH_MORE_SUBFOLDERS_VARIABLES,
  true
>('CASEMEDIA/FETCH_MORE_SUBFOLDERS_INIT');

export const FETCH_MORE_SUBFOLDERS_SUCCESS = createGraphQLSuccessAction<
  FetchSubFoldersQueryResponse,
  FETCH_MORE_SUBFOLDERS_VARIABLES,
  true
>('CASEMEDIA/FETCH_MORE_SUBFOLDERS_SUCCESS');

export const FETCH_MORE_SUBFOLDERS_FAILURE = createGraphQLFailureAction<
  [{ message: string }]
>('CASEMEDIA/FETCH_MORE_SUBFOLDERS_FAILURE');

export const FETCH_MORE_FILES_REQUEST =
  createAction<ActionFetchMoreFilesRequest>(
    'CASEMEDIA/FETCH_MORE_FILES_REQUEST'
  );
export const FETCH_MORE_FILES_INIT = 'CASEMEDIA/FETCH_MORE_FILES_INIT';
export const FETCH_MORE_FILES_SUCCESS = createGraphQLSuccessAction<
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  FetchCaseContentsQueryResponse | any, // TODO - Fix this - remove any
  ActionFetchMoreFilesRequest,
  true
>('CASEMEDIA/FETCH_MORE_FILES_SUCCESS');
export const FETCH_MORE_FILES_FAILURE = createGraphQLFailureAction<
  [{ message: string }]
>('CASEMEDIA/FETCH_MORE_FILES_FAILURE');

interface FETCH_SUB_FOLDERS_BYID_VARIABLES {
  caseId?: CaseId;
  folderId?: FolderId;
  limit: number;
  offset: number;
  isCase?: boolean;
}
export const FETCH_SUB_FOLDERS_BYID_REQUEST = createGraphQLRequestAction<
  FETCH_SUB_FOLDERS_BYID_VARIABLES,
  true
>('CASEMEDIA/FETCH_SUB_FOLDERS_BYID_REQUEST');
export const FETCH_SUB_FOLDERS_BYID_SUCCESS = createGraphQLSuccessAction<
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  FetchCaseContentsQueryResponse | FetchSubFoldersQueryResponse | any, // TODO - Fix this - remove any
  FETCH_SUB_FOLDERS_BYID_VARIABLES,
  true
>('CASEMEDIA/FETCH_SUB_FOLDERS_BYID_SUCCESS');
export const FETCH_SUB_FOLDERS_BYID_FAILURE = createGraphQLFailureAction<
  [{ message: string }]
>('CASEMEDIA/FETCH_SUB_FOLDERS_BYID_FAILURE');

export const SET_SELECTED_FOLDER_REQUEST =
  createAction<ActionSetSelectedFolderPayload>(
    'CASEMEDIA/SET_SELECTED_FOLDER_REQUEST'
  );
export const SET_SELECTED_FOLDER_SUCCESS =
  createAction<ActionSelectedFolderSuccessPayload>(
    'CASEMEDIA/SET_SELECTED_FOLDER_SUCCESS'
  );

export const SET_SELECTED_VIRTUAL_FOLDER_REQUEST = createAction<{
  folder: FolderStateType<CaseId>;
  virtualFolderName: VirtualFolderNames | undefined;
}>('CASEMEDIA/SET_SELECTED_VIRTUAL_FOLDER_REQUEST');

export const CREATE_NEW_FOLDER = createAction<ActionCreateNewFolderPayload>(
  'CASEMEDIA/CREATE_NEW_FOLDER'
);
export const CREATE_NEW_FOLDER_SUCCESS = createAction<{
  newFolderName: string;
  parentId: FolderId;
  isRoot: boolean;
  // The PARENT container's type — drives the post-create refetch query
  // (caseContents vs subfolders). Threaded from the Add dialogs through the
  // service so the saga never has to infer it (VE-25228 D5).
  parentIsCase: boolean;
}>('CASEMEDIA/CREATE_NEW_FOLDER_SUCCESS');
export const CREATE_NEW_FOLDER_FAILURE = createGraphQLFailureAction<{
  newFolderName: string;
}>('CASEMEDIA/CREATE_NEW_FOLDER_FAILURE');

export const DELETE_CASE_SDO_REQUEST = createAction<ActionDeleteCaseSdoPayload>(
  'CASEMEDIA/DELETE_CASE_SDO_REQUEST'
);

export const actionSoftDeleteCaseSdo = (
  payload: ActionSoftDeleteCaseSdoPayload
) => SOFT_DELETE_CASE_SDO_REQUEST(payload);
export const SOFT_DELETE_CASE_SDO_REQUEST =
  createAction<ActionSoftDeleteCaseSdoPayload>(
    'CASEMEDIA/SOFT_DELETE_CASE_SDO_REQUEST'
  );

export const DELETE_CASE_SDO_FAILURE =
  createGraphQLFailureAction<ActionDeleteCaseSdoPayload>(
    'CASEMEDIA/DELETE_CASE_SDO_FAILURE'
  );

export const DELETE_FOLDER_REQUEST = createAction<ActionDeleteFolderPayload>(
  'CASEMEDIA/DELETE_FOLDER_REQUEST'
);

interface DELETE_FOLDER_VARIABLES {
  id: TreeObjectId;
  folderName: string;
  isPermanentDelete?: boolean;
  suppressSnackbar?: boolean;
}
export const DELETE_FOLDER_INIT = createGraphQLRequestAction<
  DELETE_FOLDER_VARIABLES,
  true
>('CASEMEDIA/DELETE_FOLDER_INIT');
export const DELETE_FOLDER_SUCCESS = createGraphQLSuccessAction<
  DeleteFolderOrCaseQueryResponse,
  DELETE_FOLDER_VARIABLES,
  true
>('CASEMEDIA/DELETE_FOLDER_SUCCESS');
// VE-21114: payload carries the GraphQL errors so the handler can surface the
// real reason (e.g. v2 "folder not empty"); explicit dispatches pass undefined.
export const DELETE_FOLDER_FAILURE = createGraphQLFailureAction<
  ReadonlyArray<{ message?: string }> | undefined,
  DELETE_FOLDER_VARIABLES,
  true
>('CASEMEDIA/DELETE_FOLDER_FAILURE');

export const SOFT_DELETE_FOLDER_SUCCESS = createAction<{
  id: TreeObjectId;
  folderName: string;
}>('CASEMEDIA/SOFT_DELETE_FOLDER_SUCCESS');
export const SOFT_DELETE_FOLDER_FAILURE = createAction<{
  id: TreeObjectId;
  folderName: string;
}>('CASEMEDIA/SOFT_DELETE_FOLDER_FAILURE');

export const DELETE_FILE_REQUEST = createAction<ActionDeleteFilePayload>(
  'CASEMEDIA/DELETE_FILE_REQUEST'
);
export const DELETE_FILE_INIT = 'CASEMEDIA/DELETE_FILE_INIT';
export const DELETE_FILE_SUCCESS = createGraphQLSuccessAction<
  | SoftDeleteFileQueryResponse
  | DeleteFileQueryResponse
  | DeleteOutputFileQueryResponse,
  ActionDeleteFilePayload,
  true
>('CASEMEDIA/DELETE_FILE_SUCCESS');
export const DELETE_FILE_FAILURE = createGraphQLFailureAction<
  unknown,
  ActionDeleteFilePayload,
  true
>('CASEMEDIA/DELETE_FILE_FAILURE');
// Terminal signal for a soft delete: emitted once the TDO has been relocated
// into the Trash folder (or once relocation has been given up on). DELETE_FILE_SUCCESS
// only marks the tag write, which happens before the move — callers that must not tear
// down the delete saga early (e.g. deleting the file open in the editor) wait for this. VE-25125.
export const DELETE_FILE_RELOCATED = createAction<{ tdoId: TDOId }>(
  'CASEMEDIA/DELETE_FILE_RELOCATED'
);

export const CREATE_EXPORT_TDO_REQUEST =
  createAction<ActionCreateExportTdoPayload>(
    'CASEMEDIA/CREATE_EXPORT_TDO_REQUEST'
  );
export const CREATE_EXPORT_TDO_INIT = 'CASEMEDIA/CREATE_EXPORT_TDO_INIT';
export const CREATE_EXPORT_TDO_SUCCESS = createGraphQLSuccessAction<
  CreateExportFilesQueryResponse,
  { input: { parentFolderId: TreeObjectId } },
  true
>('CASEMEDIA/CREATE_EXPORT_TDO_SUCCESS');
export const CREATE_EXPORT_TDO_FAILURE = createGraphQLFailureAction(
  'CASEMEDIA/CREATE_EXPORT_TDO_FAILURE'
);

interface MOVE_FOLDER_VARIABLES {
  treeObjectId: TreeObjectId;
  isRoot: boolean;
  suppressSnackbar?: boolean;
}
export const MOVE_FOLDER_REQUEST = createAction<ActionMoveFolderPayload>(
  'CASEMEDIA/MOVE_FOLDER_REQUEST'
);
export const MOVE_FOLDER_INIT = 'CASEMEDIA/MOVE_FOLDER_INIT';
export const MOVE_FOLDER_SUCCESS = createGraphQLSuccessAction<
  MoveFolderOrCaseQueryResponse,
  MOVE_FOLDER_VARIABLES,
  true
>('CASEMEDIA/MOVE_FOLDER_SUCCESS');
export const MOVE_FOLDER_FAILURE = createGraphQLFailureAction(
  'CASEMEDIA/MOVE_FOLDER_FAILURE'
);

interface FETCH_EXPORT_TDO_VARIABLES {
  treeObjectId: TreeObjectId;
}
export const FETCH_EXPORT_TDO_REQUEST =
  createAction<ActionFetchExportTdoPayload>(
    'CASEMEDIA/FETCH_EXPORT_TDO_REQUEST'
  );
export const FETCH_EXPORT_TDO_INIT = 'CASEMEDIA/FETCH_EXPORT_TDO_INIT';
export const FETCH_EXPORT_TDO_SUCCESS = createGraphQLSuccessAction<
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  FetchExportTdoQueryResponse | any, // TODO: Fix this - remove any
  FETCH_EXPORT_TDO_VARIABLES,
  true
>('CASEMEDIA/FETCH_EXPORT_TDO_SUCCESS');
export const FETCH_EXPORT_TDO_FAILURE = createGraphQLFailureAction(
  'CASEMEDIA/FETCH_EXPORT_TDO_FAILURE'
);

export const EXPORT_FILES_REQUEST = createAction<ActionExportFilePayload>(
  'CASEMEDIA/EXPORT_FILES_REQUEST'
);
export const EXPORT_FILES_INIT = 'CASEMEDIA/EXPORT_FILES_INIT';
export const EXPORT_FILES_SUCCESS = createGraphQLSuccessAction<
  ExportFilesQueryResponse,
  {
    caseId: TreeObjectId;
    tdoId: TDOId;
    clusterId: string;
    engineId: string;
  },
  true
>('CASEMEDIA/EXPORT_FILES_SUCCESS');
export const EXPORT_FILES_FAILURE = createGraphQLFailureAction(
  'CASEMEDIA/EXPORT_FILES_FAILURE'
);

export const SET_DOWNLOAD_ALL_STATUS =
  createAction<ActionDownloadAllStatusPayload>(
    'CASEMEDIA/set_downloadAll_status'
  );

interface FETCH_DOWNLOADALL_JOB_STATUS_VARIABLES {
  jobId: string;
  exportTdoId: TDOId;
  treeObjectId: TreeObjectId;
}

export const FETCH_DOWNLOADALL_JOB_STATUS_REQUEST =
  createAction<ActionDownloadAllJobStatusPayload>(
    'CASEMEDIA/FETCH_DOWNLOADALL_JOB_STATUS_REQUEST'
  );
export const FETCH_DOWNLOADALL_JOB_STATUS_INIT =
  'CASEMEDIA/FETCH_DOWNLOADALL_JOB_STATUS_INIT';
export const FETCH_DOWNLOADALL_JOB_STATUS_SUCCESS = createGraphQLSuccessAction<
  FetchJobStatusQueryResponse,
  FETCH_DOWNLOADALL_JOB_STATUS_VARIABLES,
  true
>('CASEMEDIA/FETCH_DOWNLOADALL_JOB_STATUS_SUCCESS');
export const FETCH_DOWNLOADALL_JOB_STATUS_FAILURE = createGraphQLFailureAction(
  'CASEMEDIA/FETCH_DOWNLOADALL_JOB_STATUS_FAILURE'
);
export const FETCH_DOWNLOADALL_JOB_STATUS_COMPLETE =
  'CASEMEDIA/FETCH_DOWNLOADALL_JOB_STATUS_COMPLETE';

export const SET_SEARCH_TEXT = createAction<string>(
  'CASEMEDIA/SET_SEARCH_TEXT'
);
export const SEARCH_CASES_AND_FOLDERS = createAction(
  'CASEMEDIA/SEARCH_CASES_AND_FOLDERS'
);
export const SEARCH_CASES_AND_FOLDERS_INIT = createGraphQLRequestAction(
  'CASEMEDIA/SEARCH_CASES_AND_FOLDERS_INIT'
);
export const SEARCH_CASES_AND_FOLDERS_FAILURE = createAction<
  string | undefined
>('CASEMEDIA/SEARCH_CASES_AND_FOLDERS_FAILURE');

export const SET_SEARCH_RESULTS = createAction<{
  searchResults: CaseMediaState['searchResults'];
  searchMoreFilesOffset: number;
  searchMoreFoldersOffset: number;
  searchMoreFilesAvailable: boolean;
  searchMoreFoldersAvailable: boolean;
  cacheKey?: string;
}>('CASEMEDIA/SET_SEARCH_RESULTS');
export const RESTORE_SEARCH_RESULTS = createAction<string>(
  'CASEMEDIA/RESTORE_SEARCH_RESULTS'
);
export const SET_SEARCH_MORE_RESULTS = createAction(
  'CASEMEDIA/SET_SEARCH_MORE_RESULTS'
);
export const CLEAR_ALL_SEARCH_RESULTS = createAction(
  'CASEMEDIA/CLEAR_ALL_SEARCH_RESULTS'
);
export const SET_SEARCH_SCOPE = createAction<{
  folderId: string;
  folderName: string;
  treeObjectId?: TreeObjectId;
  isCase?: boolean;
}>('CASEMEDIA/SET_SEARCH_SCOPE');
export const CLEAR_SEARCH_SCOPE = createAction('CASEMEDIA/CLEAR_SEARCH_SCOPE');
export const SET_SEARCH_BAR_SCOPE_MODE = createAction<
  'selectedFolder' | 'everything'
>('CASEMEDIA/SET_SEARCH_BAR_SCOPE_MODE');
export const OPEN_SEARCH_DIALOG = createAction('CASEMEDIA/OPEN_SEARCH_DIALOG');
export const CLOSE_SEARCH_DIALOG = createAction(
  'CASEMEDIA/CLOSE_SEARCH_DIALOG'
);
export const FETCH_FOLDER_PERMISSIONS_REQUEST = createAction<FolderId[]>(
  'CASEMEDIA/FETCH_FOLDER_PERMISSIONS_REQUEST'
);
export const SET_FOLDER_PERMISSIONS = createAction<
  Record<string, FolderPermissionFlags>
>('CASEMEDIA/SET_FOLDER_PERMISSIONS');

export const REMOVE_SELECTED_FOLDER = createAction<{
  layer: number;
}>('CASEMEDIA/REMOVE_SELECTED_FOLDER');

export const FETCH_SELECTED_CASE_OR_FOLDER_TREE_REQUEST = createAction<{
  folderId: string;
  isCase: boolean;
}>('CASEMEDIA/FETCH_SELECTED_CASE_OR_FOLDER_TREE_REQUEST');
export const FETCH_SELECTED_CASE_OR_FOLDER_TREE_INIT =
  createGraphQLRequestAction(
    'CASEMEDIA/FETCH_SELECTED_CASE_OR_FOLDER_TREE_INIT'
  );
export const FETCH_SELECTED_CASE_OR_FOLDER_TREE_SUCCESS =
  createGraphQLSuccessAction<
    FetchCaseOrFolderTreeQueryResponse,
    {
      folderId: FolderId;
      limit: number;
      offset: number;
      isCase: boolean;
    },
    true
  >('CASEMEDIA/FETCH_SELECTED_CASE_OR_FOLDER_TREE_SUCCESS');
export const FETCH_SELECTED_CASE_OR_FOLDER_TREE_FAILURE =
  createGraphQLFailureAction<[{ name: string; message: string }]>(
    'CASEMEDIA/FETCH_SELECTED_CASE_OR_FOLDER_TREE_FAILURE'
  );

export const CLEAR_NAV_CASE = createAction('CASEMEDIA/CLEAR_NAV_CASE');
// VE-25873 Unit 3 — the parent request an editor session should land back on
// at exit (null clears it once consumed).
export const SET_EDITOR_RETURN_TARGET = createAction<
  CaseMediaState['editorReturnTarget']
>('CASEMEDIA/SET_EDITOR_RETURN_TARGET');
// VE-30022 — the folder view has been left for an editor. Payload-free: the
// reducer snapshots which columns were on screen, because only it can see them.
export const MARK_FOLDER_VIEW_LEFT = createAction(
  'CASEMEDIA/MARK_FOLDER_VIEW_LEFT'
);
export const CLEAR_ALL_SELECTED_FOLDERS = createAction(
  'CASEMEDIA/CLEAR_ALL_SELECTED_FOLDERS'
);

export const SET_SHOW_LEGACY_MEDIAS =
  createAction<ActionSetShowLegacyMediasPayload>(
    'CASEMEDIA/SET_SHOW_LEGACY_MEDIAS'
  );
export const FETCH_LEGACY_MEDIAS_REQUEST =
  createAction<ActionFetchLegacyMediasRequest>(
    'CASEMEDIA/FETCH_LEGACY_MEDIAS_REQUEST'
  );
export const FETCH_LEGACY_MEDIAS_INIT = createGraphQLRequestAction(
  'CASEMEDIA/FETCH_LEGACY_MEDIAS_INIT'
);
export const FETCH_LEGACY_MEDIAS_SUCCESS = createGraphQLSuccessAction<
  FetchLegacyMediasQueryResponse,
  ActionFetchLegacyMediasRequest,
  true
>('CASEMEDIA/FETCH_LEGACY_MEDIAS_SUCCESS');
export const FETCH_LEGACY_MEDIAS_FAILURE = createGraphQLFailureAction<
  [{ message: string }]
>('CASEMEDIA/FETCH_LEGACY_MEDIAS_FAILURE');

interface FILE_TDO_WITH_CASE_VARIABLES {
  sdoId: string;
  tdoName: string;
  tdoId: TDOId;
  newFolderId: FolderId | TreeObjectId;
  oldFolderId: FolderId | undefined;
}

export const FILE_TDO_WITH_CASE_REQUEST =
  createAction<ActionFileTdoWithCasePayload>(
    'CASEMEDIA/FILE_TDO_WITH_CASE_REQUEST'
  );
export const FILE_TDO_WITH_CASE_INIT = createGraphQLRequestAction(
  'CASEMEDIA/FILE_TDO_WITH_CASE_INIT'
);
export const FILE_TDO_WITH_CASE_SUCCESS = createGraphQLSuccessAction<
  FileTdoWithCaseQueryResponse | MoveTdoQueryResponse,
  ActionFileTdoWithCasePayload,
  true
>('CASEMEDIA/FILE_TDO_WITH_CASE_SUCCESS');
export const FILE_TDO_WITH_CASE_FAILURE = createGraphQLFailureAction<
  [{ message: string }],
  FILE_TDO_WITH_CASE_VARIABLES,
  true
>('CASEMEDIA/FILE_TDO_WITH_CASE_FAILURE');

export const DELETE_MEDIA_FILE_SDO_REQUEST =
  createAction<ActionDeleteMediaFileSdoPayload>(
    'CASEMEDIA/DELETE_MEDIA_FILE_SDO_REQUEST'
  );
export const DELETE_MEDIA_FILE_SDO_SUCCESS =
  createAction<ActionDeleteMediaFileSdoPayload>(
    'CASEMEDIA/DELETE_MEDIA_FILE_SDO_SUCCESS'
  );

// TODO: The type should be more specific
export const DELETE_MEDIA_FILE_SDO_FAILURE = createGraphQLFailureAction<
  { message: string }[],
  { tdoName: string },
  true
>('CASEMEDIA/DELETE_MEDIA_FILE_SDO_FAILURE');

export const FETCH_TDO_DETAIL_FOR_SDOS_REQUEST =
  createAction<ActionFetchTdoDetailForSdosPayload>(
    'CASEMEDIA/FETCH_TDO_DETAIL_FOR_SDOS_REQUEST'
  );
export const FETCH_TDO_DETAIL_FOR_SDOS_SUCCESS = createGraphQLSuccessAction<
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  FetchTdoDetailForSdosResponse | any // TODO: Fix this - remove any
>('CASEMEDIA/FETCH_TDO_DETAIL_FOR_SDOS_SUCCESS');
export const FETCH_TDO_DETAIL_FOR_SDOS_FAILURE = createGraphQLFailureAction<
  [{ message: string }]
>('CASEMEDIA/FETCH_TDO_DETAIL_FOR_SDOS_FAILURE');

// VE-28061: re-read the output assets (redacted media, audit logs, redacted
// transcripts) of the given files and patch them onto their rows in place, so a
// burn-in that finished while the folder view was up flips the row from
// "Redacting" to "Redacted" without reloading the folder. Dispatched by the
// casemedia saga on REDACTED_FILE_COMPLETED.
export const REFRESH_FILE_OUTPUTS_REQUEST = createAction<{ tdoIds: TDOId[] }>(
  'CASEMEDIA/REFRESH_FILE_OUTPUTS_REQUEST'
);
export const REFRESH_FILE_OUTPUTS_INIT = createGraphQLRequestAction(
  'CASEMEDIA/REFRESH_FILE_OUTPUTS_INIT'
);
export const REFRESH_FILE_OUTPUTS_SUCCESS =
  createGraphQLSuccessAction<StalenessAssetsResponse>(
    'CASEMEDIA/REFRESH_FILE_OUTPUTS_SUCCESS'
  );
export const REFRESH_FILE_OUTPUTS_FAILURE = createGraphQLFailureAction<
  [{ message: string }]
>('CASEMEDIA/REFRESH_FILE_OUTPUTS_FAILURE');

export const RENAME_FOLDER_REQUEST = createAction<ActionRenameFolderPayload>(
  'CASEMEDIA/RENAME_FOLDER_REQUEST'
);
export const RENAME_FOLDER_INIT = createGraphQLRequestAction(
  'CASEMEDIA/RENAME_FOLDER_INIT'
);
export const RENAME_FOLDER_SUCCESS =
  createGraphQLSuccessAction<RenameFolderQueryResponse>(
    'CASEMEDIA/RENAME_FOLDER_SUCCESS'
  );
export const RENAME_FOLDER_FAILURE = createGraphQLFailureAction<
  [{ message: string }]
>('CASEMEDIA/RENAME_FOLDER_FAILURE');

export const SET_MOVE_EXPANDED_FOLDER_REQUEST = createAction<{
  expandedFolder: FolderStateType;
}>('CASEMEDIA/SET_MOVE_EXPANDED_FOLDER__REQUEST');
export const SET_SOURCE_FOLDER_TO_MOVE_REQUEST = createAction<{
  sourceFolderToMove: FolderStateType;
  moveFromFolder: FolderStateType;
}>('CASEMEDIA/SET_SOURCE_FOLDER_TO_MOVE_REQUEST');
export const CLEAR_ALL_MOVE_FOLDER_STATE_REQUEST = createAction(
  'CASEMEDIA/CLEAR_ALL_MOVE_FOLDER_STATE_REQUEST'
);
export const CLEAR_FROM_MOVE_EXPANDED_REQUEST = createAction<{
  folderId: FolderId;
}>('CASEMEDIA/CLEAR_FROM_MOVE_EXPANDED_REQUEST');

interface FETCH_FOR_EXPANDED_FOLDER_VARIABLES {
  folderId: FolderId;
  limit: number;
  offset: number;
}
export const FETCH_FOR_EXPANDED_FOLDER_REQUEST =
  createAction<ActionFetchForExportedFolderPayload>(
    'CASEMEDIA/FETCH_FOR_EXPANDED_FOLDER_REQUEST'
  );
export const FETCH_FOR_EXPANDED_FOLDER_INIT = createGraphQLRequestAction<
  FETCH_FOR_EXPANDED_FOLDER_VARIABLES,
  true
>('CASEMEDIA/FETCH_FOR_EXPANDED_FOLDER_INIT');
export const FETCH_FOR_EXPANDED_FOLDER_SUCCESS = createGraphQLSuccessAction<
  FetchSubFoldersQueryResponse,
  FETCH_FOR_EXPANDED_FOLDER_VARIABLES,
  true
>('CASEMEDIA/FETCH_FOR_EXPANDED_FOLDER_SUCCESS');
export const FETCH_FOR_EXPANDED_FOLDER_FAILURE = createGraphQLFailureAction<
  [{ message: string }],
  FETCH_FOR_EXPANDED_FOLDER_VARIABLES,
  true
>('CASEMEDIA/FETCH_FOR_EXPANDED_FOLDER_FAILURE');

export const SET_SOURCE_FILE_TO_MOVE_REQUEST =
  createAction<ActionSetSourceFileToMovePayload>(
    'CASEMEDIA/SET_SOURCE_FILE_TO_MOVE_REQUEST'
  );

interface SEARCH_NAME_EXISTS_IN_FOLDER_VARIABLES {
  folderId: FolderId;
  name: string;
  actionName: SearchNameActions;
  actionPayload:
    | ActionCreateNewFolderPayload
    | ActionMoveFolderPayload
    | ActionRenameFolderPayload
    | ActionRestoredFolderPayload;
  limit: number;
  offset: number;
}
export const SEARCH_NAME_EXISTS_IN_FOLDER_REQUEST =
  createAction<SearchNameExistsInFolderRequestPayload>(
    'CASEMEDIA/SEARCH_NAME_EXISTS_IN_FOLDER_REQUEST'
  );
export const SEARCH_NAME_EXISTS_IN_FOLDER_INIT = createGraphQLRequestAction(
  'CASEMEDIA/SEARCH_NAME_EXISTS_IN_FOLDER_INIT'
);
export const SEARCH_NAME_EXISTS_IN_FOLDER_SUCCESS = createGraphQLSuccessAction<
  SearchNameExistInFolderQueryResponse,
  SEARCH_NAME_EXISTS_IN_FOLDER_VARIABLES,
  true
>('CASEMEDIA/SEARCH_NAME_EXISTS_IN_FOLDER_SUCCESS');
export const SEARCH_NAME_EXISTS_IN_FOLDER_FAILURE = createGraphQLFailureAction<
  [{ message: string }],
  SEARCH_NAME_EXISTS_IN_FOLDER_VARIABLES,
  true
>('CASEMEDIA/SEARCH_NAME_EXISTS_IN_FOLDER_FAILURE');

export const SET_SELECTED_REQUEST_TO_INGEST = createAction<{
  selectedRequestToIngest: CaseId | FolderId;
}>('CASEMEDIA/SET_SELECTED_REQUEST_TO_INGEST ');
export const CLEAR_SELECTED_REQUEST_TO_INGEST = createAction(
  'CASEMEDIA/CLEAR_SELECTED_REQUEST_TO_INGEST '
);

export const SET_CASE_MEDIA_STATUS = createAction<ActionSetCaseMedia>(
  'CASEMEDIA/SET_CASE_MEDIA_STATUS'
);
// Mirror the `recorded` the PDF fast path has already written
// server-side onto the displayed folder row. The same field a re-fetch writes,
// so a re-fetch corrects it — unlike the `recordedTdoIds` override it replaced.
export const MARK_TDO_RECORDED = createAction<{ tdoId: string }>(
  'CASEMEDIA/MARK_TDO_RECORDED'
);
export const MARK_FOLDER_VIEW_TDO_OPENED =
  createAction<ActionMarkFolderViewTdoOpenedPayload>(
    'CASEMEDIA/MARK_FOLDER_VIEW_TDO_OPENED'
  );
export const SET_LAST_ACTIVE_FOLDER_VIEW_FILE =
  createAction<ActionSetLastActiveFolderViewFilePayload>(
    'CASEMEDIA/SET_LAST_ACTIVE_FOLDER_VIEW_FILE'
  );
export const CONSUME_FOLDER_VIEW_RETURN_SCROLL = createAction(
  'CASEMEDIA/CONSUME_FOLDER_VIEW_RETURN_SCROLL'
);
export const CLEAR_LAST_ACTIVE_FOLDER_VIEW_FILE = createAction(
  'CASEMEDIA/CLEAR_LAST_ACTIVE_FOLDER_VIEW_FILE'
);
export const CLEAR_LAST_OPENED_FOLDER_VIEW_FILE_FOR_CASE =
  createAction<ActionClearLastOpenedFolderViewFileForCasePayload>(
    'CASEMEDIA/CLEAR_LAST_OPENED_FOLDER_VIEW_FILE_FOR_CASE'
  );
export const UPDATE_REDACTION_STATUS_CHANGE =
  createAction<ActionUpdateRedactionStatusChangePayload>(
    'CASEMEDIA/UPDATE_REDACTION_STATUS_CHANGE '
  );
export const UPDATE_REDACTION_STATUS_REQUEST =
  createAction<ActionUpdateRedactionStatusRequestPayload>(
    'CASEMEDIA/UPDATE_REDACTION_STATUS_REQUEST'
  );
export const UPDATE_REDACTION_STATUS_INIT =
  createGraphQLRequestAction<ActionUpdateRedactionStatusRequestPayload>(
    'CASEMEDIA/UPDATE_REDACTION_STATUS_INIT'
  );
export const UPDATE_REDACTION_STATUS_SUCCESS =
  createGraphQLSuccessAction<UpdateRedactionStatusQueryResponse>(
    'CASEMEDIA/UPDATE_REDACTION_STATUS_SUCCESS'
  );
export const UPDATE_REDACTION_STATUS_FAILURE = createGraphQLFailureAction<
  [{ message: string }],
  ActionUpdateRedactionStatusRequestPayload
>('CASEMEDIA/UPDATE_REDACTION_STATUS_FAILURE');

export const MOVE_FILE_TO_CASE_REQUEST =
  createAction<ActionMoveFileToCasePayload>(
    'CASEMEDIA/MOVE_FILE_TO_CASE_REQUEST'
  );
export const MOVE_FILE_TO_CASE_INIT = createGraphQLRequestAction(
  'CASEMEDIA/MOVE_FILE_TO_CASE_INIT'
);
export const MOVE_FILE_TO_CASE_SUCCESS = createGraphQLSuccessAction<
  MoveTdoQueryResponse,
  ActionMoveFileToCasePayload,
  true
>('CASEMEDIA/MOVE_FILE_TO_CASE_SUCCESS');
export const MOVE_FILE_TO_CASE_FAILURE = createGraphQLFailureAction(
  'CASEMEDIA/MOVE_FILE_TO_CASE_FAILURE'
);
export const SET_SOURCE_INPUT_FILE_TO_MOVE =
  createAction<ActionSetMoveInputFilePayload>(
    'CASEMEDIA/SET_SOURCE_INPUT_FILE_TO_MOVE'
  );
export const TDO_IN_USE = createAction<{
  messageId: string;
}>('CASEMEDIA/TDO_IN_USE');

export const actionSetCaseMediaStatus = (payload: ActionSetCaseMedia) =>
  SET_CASE_MEDIA_STATUS(payload);

export const actionMarkTdoRecorded = (tdoId: string) =>
  MARK_TDO_RECORDED({ tdoId });

export const actionMarkFolderViewTdoOpened = (tdoId: TDOId) =>
  MARK_FOLDER_VIEW_TDO_OPENED({ tdoId });

export const actionMarkLastActiveFolderViewFileForNavigation = (
  caseId: CaseId,
  tdoId: TDOId
) =>
  SET_LAST_ACTIVE_FOLDER_VIEW_FILE({
    caseId,
    tdoId,
    isReturnScrollPending: true,
  });

export const actionMarkLastActiveFolderViewFileInPlace = (
  caseId: CaseId,
  tdoId: TDOId
) =>
  SET_LAST_ACTIVE_FOLDER_VIEW_FILE({
    caseId,
    tdoId,
    isReturnScrollPending: false,
  });

export const actionConsumeFolderViewReturnScroll = () =>
  CONSUME_FOLDER_VIEW_RETURN_SCROLL();

export const actionClearLastActiveFolderViewFile = () =>
  CLEAR_LAST_ACTIVE_FOLDER_VIEW_FILE();

export const actionClearLastOpenedFolderViewFileForCase = (
  caseId: CaseId,
  tdoId?: TDOId
) => CLEAR_LAST_OPENED_FOLDER_VIEW_FILE_FOR_CASE({ caseId, tdoId });

export const actionCreateRootFolder = () => CREATE_ROOT_FOLDER_REQUEST();

export const actionSetRootFolderSmartSelection = (enabled: boolean) =>
  SET_ROOT_FOLDER_SMART_SELECTION(enabled);

export const actionFetchFolderRequest = (payload: ActionFetchFolderRequest) =>
  FETCH_ROOT_FOLDERS_REQUEST(payload);

export const actionFetchMoreRootFoldersRequest = (
  payload: ActionFetchFolderRequest
) => FETCH_MORE_ROOTFOLDERS_REQUEST(payload);

export const actionFetchMoreSubFoldersRequest = (
  payload: ActionFetchMoreSubFoldersRequest
) => FETCH_MORE_SUBFOLDERS_REQUEST(payload);

export const actionFetchMoreFilesRequest = (
  payload: ActionFetchMoreFilesRequest
) => FETCH_MORE_FILES_REQUEST(payload);

// TODO: unused?
// export const actionFetchFoldersById = (payload: ActionFetchFolderByIdPayload) =>
//   FETCH_SUB_FOLDERS_BYID_REQUEST(payload);

export const actionSetSelectedVirtualFolder = (
  payload: ActionSetSelectedVirtualFolderPayload
) => SET_SELECTED_VIRTUAL_FOLDER_REQUEST(payload);

export const actionSetSelectedFolder = (
  payload: ActionSetSelectedFolderPayload
) => SET_SELECTED_FOLDER_REQUEST(payload);

export const actionSetSelectedFolderSuccess = (
  payload: ActionSelectedFolderSuccessPayload
) => SET_SELECTED_FOLDER_SUCCESS(payload);

export const actionDeleteFolder = (payload: ActionDeleteFolderPayload) =>
  DELETE_FOLDER_REQUEST(payload);

export const actionSoftDeleteFolder = (payload: {
  folderId: FolderId;
  treeObjectId: TreeObjectId;
  folderName: string;
  oldFolderPath: FolderPathItem[];
}) => SOFT_DELETE_FOLDER_REQUEST(payload);
export const SOFT_DELETE_FOLDER_REQUEST = createAction<{
  folderId: FolderId;
  treeObjectId: TreeObjectId;
  folderName: string;
  oldFolderPath: FolderPathItem[];
}>('CASEMEDIA/SOFT_DELETE_FOLDER_REQUEST');

export const actionDeleteCaseSdo = (payload: ActionDeleteCaseSdoPayload) =>
  DELETE_CASE_SDO_REQUEST(payload);

export const actionDeleteFile = (payload: ActionDeleteFilePayload) =>
  DELETE_FILE_REQUEST(payload);

export const actionCreateExportTdoRequest = (
  payload: ActionCreateExportTdoPayload
) => CREATE_EXPORT_TDO_REQUEST(payload);

export const actionExportFilesRequest = (payload: ActionExportFilePayload) =>
  EXPORT_FILES_REQUEST(payload);

export const actionDownloadAllJobStatusRequest = (
  payload: ActionDownloadAllJobStatusPayload
) => FETCH_DOWNLOADALL_JOB_STATUS_REQUEST(payload);

export const actionFetchExportTdoRequest = (
  payload: ActionFetchExportTdoPayload
) => FETCH_EXPORT_TDO_REQUEST(payload);

export const actionDownloadAllStatus = (
  payload: ActionDownloadAllStatusPayload
) => SET_DOWNLOAD_ALL_STATUS(payload);

export const actionCreateNewFolder = (payload: ActionCreateNewFolderPayload) =>
  CREATE_NEW_FOLDER(payload);

export const actionMoveFolderRequest = (payload: ActionMoveFolderPayload) =>
  MOVE_FOLDER_REQUEST(payload);

export const actionRemoveSelectedFolder = (
  payload: ActionRemoveSelectedFolderPayload
) => REMOVE_SELECTED_FOLDER(payload);

export const actionfetchSelectedCaseOrFolderTree = (payload: {
  folderId: string;
  isCase: boolean;
}) => FETCH_SELECTED_CASE_OR_FOLDER_TREE_REQUEST(payload);

export const actionNavToCaseMedia = (payload: ActionNavToCaseMediaPayload) =>
  ROUTE_HOME(payload);

export const actionClearNavCase = CLEAR_NAV_CASE;

export const actionSetEditorReturnTarget = (
  target: CaseMediaState['editorReturnTarget']
) => SET_EDITOR_RETURN_TARGET(target);

export const actionMarkFolderViewLeft = () => MARK_FOLDER_VIEW_LEFT();

export const actionClearAllSelectedFolders = () => CLEAR_ALL_SELECTED_FOLDERS();

export const actionSetSearchText = (newText: string) =>
  SET_SEARCH_TEXT(newText);

export const actionSearchCasesFolders = SEARCH_CASES_AND_FOLDERS;

export const actionSetSearchResults = (payload: {
  searchResults: CaseMediaState['searchResults'];
  searchMoreFilesOffset: number;
  searchMoreFoldersOffset: number;
  searchMoreFilesAvailable: boolean;
  searchMoreFoldersAvailable: boolean;
  cacheKey?: string;
}) => SET_SEARCH_RESULTS(payload);

export const actionRestoreSearchResults = (cacheKey: string) =>
  RESTORE_SEARCH_RESULTS(cacheKey);

export const actionSearchMoreResults = () => SET_SEARCH_MORE_RESULTS();

export const actionClearAllSearchResults = () => CLEAR_ALL_SEARCH_RESULTS();

export const actionSetSearchScope = (
  folderId: string,
  folderName: string,
  treeObjectId?: TreeObjectId,
  isCase?: boolean
) => SET_SEARCH_SCOPE({ folderId, folderName, treeObjectId, isCase });
export const actionClearSearchScope = () => CLEAR_SEARCH_SCOPE();
export const actionSetSearchBarScopeMode = (
  mode: 'selectedFolder' | 'everything'
) => SET_SEARCH_BAR_SCOPE_MODE(mode);
export const actionOpenSearchDialog = () => OPEN_SEARCH_DIALOG();
export const actionCloseSearchDialog = () => CLOSE_SEARCH_DIALOG();

export const actionSetShowLegacyMedias = (
  payload: ActionSetShowLegacyMediasPayload
) => SET_SHOW_LEGACY_MEDIAS(payload);

export const actionFetchAllLegacyMedias = (
  payload: ActionFetchLegacyMediasRequest
) => FETCH_LEGACY_MEDIAS_REQUEST(payload);

export const actionFileTdoWithCaseRequest = (
  payload: ActionFileTdoWithCasePayload
) => FILE_TDO_WITH_CASE_REQUEST(payload);

export const actionDeleteMediaFileSdo = (
  payload: ActionDeleteMediaFileSdoPayload
) => DELETE_MEDIA_FILE_SDO_REQUEST(payload);

export const actionFetchTdoDetailForSdos = (
  payload: ActionFetchTdoDetailForSdosPayload
) => FETCH_TDO_DETAIL_FOR_SDOS_REQUEST(payload);

export const actionRefreshFileOutputs = (payload: { tdoIds: TDOId[] }) =>
  REFRESH_FILE_OUTPUTS_REQUEST(payload);

export const actionRenameFolder = (payload: ActionRenameFolderPayload) =>
  RENAME_FOLDER_REQUEST(payload);

export const actionSetMoveExpandedFolderRequest = (payload: {
  expandedFolder: FolderStateType;
}) => SET_MOVE_EXPANDED_FOLDER_REQUEST(payload);

export const actionSetSourceFolderToMoveRequest = (payload: {
  sourceFolderToMove: FolderStateType;
  moveFromFolder: FolderStateType;
}) => SET_SOURCE_FOLDER_TO_MOVE_REQUEST(payload);

export const actionClearAllMoveFolderStateRequest = () =>
  CLEAR_ALL_MOVE_FOLDER_STATE_REQUEST();

export const actionClearFromExpanded = (payload: { folderId: FolderId }) =>
  CLEAR_FROM_MOVE_EXPANDED_REQUEST(payload);

export const actionFetchForExpandedFolders = (
  payload: ActionFetchForExportedFolderPayload
) => FETCH_FOR_EXPANDED_FOLDER_REQUEST(payload);

export const actionSetSourceFileToMoveRequest = (
  payload: ActionSetSourceFileToMovePayload
) => SET_SOURCE_FILE_TO_MOVE_REQUEST(payload);

export const actionSearchNameExistsInFolderRequest = (
  payload: SearchNameExistsInFolderRequestPayload
) => SEARCH_NAME_EXISTS_IN_FOLDER_REQUEST(payload);

export const actionSetSelectedRequestToIngest = (payload: {
  selectedRequestToIngest: CaseId | FolderId;
}) => SET_SELECTED_REQUEST_TO_INGEST(payload);

export const actionClearSelectedRequestToIngest = () =>
  CLEAR_SELECTED_REQUEST_TO_INGEST();

export const actionUpdateRedactionStatusChange = (
  payload: ActionUpdateRedactionStatusChangePayload
) => UPDATE_REDACTION_STATUS_CHANGE(payload);

export const actionUpdateRedactionStatusRequest = (
  payload: ActionUpdateRedactionStatusRequestPayload
) => UPDATE_REDACTION_STATUS_REQUEST(payload);

export const actionSetSourceInputFileToMove = (
  payload: ActionSetMoveInputFilePayload
) => SET_SOURCE_INPUT_FILE_TO_MOVE(payload);

export const actionMoveFileToCaseRequest = (
  payload: ActionMoveFileToCasePayload
) => MOVE_FILE_TO_CASE_REQUEST(payload);

// VE-25098: folder-view multi-file selection ------------------------------

// Ctrl/Cmd+Click — toggle a single file in the selection. Also becomes the
// anchor for a subsequent Shift+Click range. `caseId` scopes the selection;
// toggling a file in a different case resets the selection to that case.
export const TOGGLE_FILE_MULTI_SELECT = createAction<{
  file: { id: TDOId; name: string };
  caseId: CaseId;
}>('CASEMEDIA/TOGGLE_FILE_MULTI_SELECT');
export const actionToggleFileMultiSelect = (payload: {
  file: { id: TDOId; name: string };
  caseId: CaseId;
}) => TOGGLE_FILE_MULTI_SELECT(payload);

// Shift+Click — select the contiguous range (from the anchor to the clicked
// file) out of the ordered visible input files. Replaces the current
// selection with that range and keeps the existing anchor.
export const SELECT_FILE_MULTI_RANGE = createAction<{
  files: { id: TDOId; name: string }[];
  caseId: CaseId;
}>('CASEMEDIA/SELECT_FILE_MULTI_RANGE');
export const actionSelectFileMultiRange = (payload: {
  files: { id: TDOId; name: string }[];
  caseId: CaseId;
}) => SELECT_FILE_MULTI_RANGE(payload);

export const CLEAR_FILE_MULTI_SELECT = createAction(
  'CASEMEDIA/CLEAR_FILE_MULTI_SELECT'
);
export const actionClearFileMultiSelect = () => CLEAR_FILE_MULTI_SELECT();

export const BATCH_TRASH_FILES_REQUEST =
  createAction<ActionBatchTrashFilesPayload>(
    'CASEMEDIA/BATCH_TRASH_FILES_REQUEST'
  );
export const actionBatchTrashFiles = (payload: ActionBatchTrashFilesPayload) =>
  BATCH_TRASH_FILES_REQUEST(payload);

export const BATCH_MOVE_FILES_REQUEST =
  createAction<ActionBatchMoveFilesPayload>(
    'CASEMEDIA/BATCH_MOVE_FILES_REQUEST'
  );
export const actionBatchMoveFiles = (payload: ActionBatchMoveFilesPayload) =>
  BATCH_MOVE_FILES_REQUEST(payload);

// Optimistically hide the given input-file ids from the list while a batch
// trash/move runs; release un-hides ids whose operation failed.
export const SET_PENDING_REMOVAL_FILES = createAction<TDOId[]>(
  'CASEMEDIA/SET_PENDING_REMOVAL_FILES'
);
export const actionSetPendingRemovalFiles = (tdoIds: TDOId[]) =>
  SET_PENDING_REMOVAL_FILES(tdoIds);

export const RELEASE_PENDING_REMOVAL_FILES = createAction<TDOId[]>(
  'CASEMEDIA/RELEASE_PENDING_REMOVAL_FILES'
);
export const actionReleasePendingRemovalFiles = (tdoIds: TDOId[]) =>
  RELEASE_PENDING_REMOVAL_FILES(tdoIds);

export const CLEAR_PENDING_REMOVAL_FILES = createAction(
  'CASEMEDIA/CLEAR_PENDING_REMOVAL_FILES'
);
export const actionClearPendingRemovalFiles = () =>
  CLEAR_PENDING_REMOVAL_FILES();

// VE-26484: stop hiding folders whose move into Trash has settled. The ids are
// added by SOFT_DELETE_FOLDER_SUCCESS (see the reducer) and released here once
// the relocation commits, so a later root refetch reflects server truth again.
export const RELEASE_PENDING_TRASH_FOLDERS = createAction<TreeObjectId[]>(
  'CASEMEDIA/RELEASE_PENDING_TRASH_FOLDERS'
);
export const actionReleasePendingTrashFolders = (ids: TreeObjectId[]) =>
  RELEASE_PENDING_TRASH_FOLDERS(ids);

// Remove input files from the open case/virtual-folder state without a refetch
// (used to finalize a successful batch move).
export const REMOVE_CASE_INPUT_FILES = createAction<TDOId[]>(
  'CASEMEDIA/REMOVE_CASE_INPUT_FILES'
);
export const actionRemoveCaseInputFiles = (tdoIds: TDOId[]) =>
  REMOVE_CASE_INPUT_FILES(tdoIds);

export const BATCH_UPDATE_STATUS_REQUEST =
  createAction<ActionBatchUpdateStatusPayload>(
    'CASEMEDIA/BATCH_UPDATE_STATUS_REQUEST'
  );
export const actionBatchUpdateFileStatus = (
  payload: ActionBatchUpdateStatusPayload
) => BATCH_UPDATE_STATUS_REQUEST(payload);

// VE-24693 (AC#10): apply watermark(s) to every selected document by writing a
// snapshot into each TDO's redaction-data asset. Burned in on that doc's export.
export const BATCH_APPLY_WATERMARK_REQUEST =
  createAction<ActionBatchApplyWatermarkPayload>(
    'CASEMEDIA/BATCH_APPLY_WATERMARK_REQUEST'
  );
export const actionBatchApplyWatermark = (
  payload: ActionBatchApplyWatermarkPayload
) => BATCH_APPLY_WATERMARK_REQUEST(payload);

// Tracks an in-flight batch operation for the progress spinner in the
// selection bar (null when idle).
export const SET_BATCH_OPERATION = createAction<BatchOperationState | null>(
  'CASEMEDIA/SET_BATCH_OPERATION'
);
export const actionSetBatchOperation = (payload: BatchOperationState | null) =>
  SET_BATCH_OPERATION(payload);

export const CHANGE_FILE_FILTERS = createAction<ActionChangeFileFiltersPayload>(
  'CASEMEDIA/CHANGE_FILE_FILTERS'
);

export const actionChangeFileFilters = (
  payload: ActionChangeFileFiltersPayload
) => CHANGE_FILE_FILTERS(payload);

// List rows vs thumbnail-tile grid for a case's input files.
export const CHANGE_FILE_VIEW_MODE = createAction<FileViewMode>(
  'CASEMEDIA/CHANGE_FILE_VIEW_MODE'
);

export const actionChangeFileViewMode = (payload: FileViewMode) =>
  CHANGE_FILE_VIEW_MODE(payload);

// VE-26943: collapse/expand the folder sidebar in the two-panel (Grid /
// Details) layouts. Collapsed is "wide screen mode" — the open request fills
// the row; expanded is the ordinary two-panel layout. The reducer routes this
// to Unassigned Media's own preference or the shared one, since the two carry
// opposite defaults.
export const SET_SIDEBAR_COLLAPSED = createAction<boolean>(
  'CASEMEDIA/SET_SIDEBAR_COLLAPSED'
);

export const actionSetSidebarCollapsed = (payload: boolean) =>
  SET_SIDEBAR_COLLAPSED(payload);

export const SET_DOWNLOAD_ALL_FOR_TDO =
  createAction<ActionDownloadAllForTdoPayload>(
    'CASEMEDIA/set_downloadAll_for_Tdo'
  );

export const actionSetDownloadAllForTdo = (
  payload: ActionDownloadAllForTdoPayload
) => SET_DOWNLOAD_ALL_FOR_TDO(payload);

export const EXPORT_MEDIA_LIST_REQUEST =
  createAction<ActionExportMediaListPayload>(
    'CASEMEDIA/EXPORT_MEDIA_LIST_REQUEST'
  );
export const EXPORT_MEDIA_LIST_SUCCESS = 'CASEMEDIA/EXPORT_MEDIA_LIST_SUCCESS';
export const EXPORT_MEDIA_LIST_FAILURE = 'CASEMEDIA/EXPORT_MEDIA_LIST_FAILURE';

export const actionExportMediaListRequest = (
  payload: ActionExportMediaListPayload
) => EXPORT_MEDIA_LIST_REQUEST(payload);

export const CHECK_EMPTY_FOLDER_REQUEST =
  'CASEMEDIA/CHECK_EMPTY_FOLDER_REQUEST';
export const CHECK_EMPTY_FOLDER_SUCCESS =
  'CASEMEDIA/CHECK_EMPTY_FOLDER_SUCCESS';
export const CHECK_EMPTY_FOLDER_FAILURE =
  'CASEMEDIA/CHECK_EMPTY_FOLDER_FAILURE';

export const LINK_FOLDER_AND_CASE_SDO = 'CASEMEDIA/LINK_FOLDER_AND_CASE_SDO';
export const LINK_FOLDER_AND_CASE_SDO_SUCCESS =
  'CASEMEDIA/LINK_FOLDER_AND_CASE_SDO_SUCCESS';
export const LINK_FOLDER_AND_CASE_SDO_FAILURE =
  'CASEMEDIA/LINK_FOLDER_AND_CASE_SDO_FAILURE';

/**
 * VE-26730: load a listed file's latest status-history row, so the folder view
 * knows whether it is carrying a correction note.
 *
 * The folder view never opens a file, so nothing has ever fetched that row
 * here — the note-addressed prompt could not fire from a row. Dispatched when
 * a row's status menu opens: one read, on a deliberate gesture, for a file the
 * server already returned in this folder's listing (the saga re-checks that).
 */
export const ENSURE_FILE_STATUS_HISTORY = createAction<{ tdoId: string }>(
  'CASEMEDIA/ENSURE_FILE_STATUS_HISTORY'
);

export const actionEnsureFileStatusHistory = (payload: { tdoId: string }) =>
  ENSURE_FILE_STATUS_HISTORY(payload);
